/* frame renderer — backdrop, entities, HUD, overlays. World space under the camera transform; device space where noted */
import { BEAST_ART, BOSS_WORMS, DELIVERY_MAX, GROUND, H, isBossWave, laserReach, W, WEAPON_CRATE_BOOST, WEAPON_CRATE_SECONDS, WORM_TIERS } from "./constants";

export function renderFrame(g, ctx, { canvas, viewRef, phaseRef, pausedRef, bgRef, crowRef, wormsRef, doloRef, spriteRef, beastRef, bossRef }) {
      const { scale, camX } = viewRef.current;
      const shake = phaseRef.current === "running" && g.worm < 100 ? (100 - g.worm) * 0.05 : 0;
      ctx.save();
      if (shake) ctx.translate((Math.random() - 0.5) * shake * 8, (Math.random() - 0.5) * shake * 6);
      ctx.scale(scale, scale);
      ctx.translate(-camX, 0);

      // ---------- backdrop ----------
      const bgImg = bgRef.current;
      if (bgImg && bgImg.width) {
        // official highway artwork fills the whole canvas, mirror-wrapped for an endless scroll
        const bgW = 967;
        const bgH = 540;
        const off = ((g.distance * 2.2) % bgW + bgW) % bgW;
        ctx.drawImage(bgImg, -off, 0, bgW, bgH);
        ctx.save();
        ctx.scale(-1, 1);
        ctx.drawImage(bgImg, off - 2 * bgW, 0, bgW, bgH);
        ctx.restore();
        // running lane marker
        ctx.strokeStyle = "rgba(255, 190, 90, 0.28)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(-20, GROUND);
        ctx.lineTo(W + 20, GROUND);
        ctx.stroke();
        // embers drifting over the art
        g.ash.forEach((a) => {
          a.y += a.s;
          a.x -= 0.4;
          if (a.y > H) { a.y = -6; a.x = Math.random() * W; }
          if (a.x < -6) a.x = W + 6;
          ctx.fillStyle = "rgba(255, 190, 120, 0.3)";
          ctx.fillRect(a.x, a.y, a.r, a.r);
        });
      } else {
      // sky — scorched apocalypse gradient, cover-art palette (procedural fallback)
      const sky = ctx.createLinearGradient(0, 0, 0, GROUND);
      sky.addColorStop(0, "#5e0f06");
      sky.addColorStop(0.45, "#a82a0a");
      sky.addColorStop(0.78, "#e05c12");
      sky.addColorStop(1, "#ffc94d");
      ctx.fillStyle = sky;
      ctx.fillRect(-20, -20, W + 40, GROUND + 20);
      ctx.fillStyle = "#2e1308";
      ctx.fillRect(-20, GROUND, W + 40, H - GROUND + 20);

      // scorched sun low over the wasteland
      const sunGlow = ctx.createRadialGradient(700, 128, 8, 700, 128, 130);
      sunGlow.addColorStop(0, "rgba(255, 233, 168, 0.9)");
      sunGlow.addColorStop(0.35, "rgba(255, 180, 60, 0.35)");
      sunGlow.addColorStop(1, "rgba(255, 120, 20, 0)");
      ctx.fillStyle = sunGlow;
      ctx.beginPath();
      ctx.arc(700, 128, 130, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(700, 128, 34, 0, Math.PI * 2);
      ctx.fillStyle = "#ffe9a8";
      ctx.fill();

      // grand canyon mesas — far layer
      g.mesas.forEach((m) => {
        const mx = ((m.x - g.distance * 2) % (W + 320) + (W + 320)) % (W + 320) - 160;
        ctx.fillStyle = m.c;
        ctx.beginPath();
        ctx.moveTo(mx, GROUND);
        ctx.lineTo(mx + m.w * 0.18, GROUND - m.h);
        ctx.lineTo(mx + m.w * 0.82, GROUND - m.h);
        ctx.lineTo(mx + m.w, GROUND);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "rgba(255, 170, 70, 0.3)";
        ctx.fillRect(mx + m.w * 0.18, GROUND - m.h, m.w * 0.64, 4);
        ctx.fillStyle = "rgba(60, 16, 6, 0.5)";
        ctx.fillRect(mx + m.w * 0.42, GROUND - m.h + 8, 3, m.h - 8);
      });

      // destroyed towers — jagged broken silhouettes with burnt window specks
      g.skyline.forEach((b) => {
        const bx = ((b.x - g.distance * 3) % (W + 160) + (W + 160)) % (W + 160) - 80;
        ctx.fillStyle = "#3d1a10";
        ctx.beginPath();
        ctx.moveTo(bx, GROUND);
        ctx.lineTo(bx, GROUND - b.h + 14);
        ctx.lineTo(bx + b.w * 0.3, GROUND - b.h);
        ctx.lineTo(bx + b.w * 0.55, GROUND - b.h + 20);
        ctx.lineTo(bx + b.w * 0.8, GROUND - b.h + 6);
        ctx.lineTo(bx + b.w, GROUND - b.h + 18);
        ctx.lineTo(bx + b.w, GROUND);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "rgba(255, 160, 60, 0.16)";
        for (let wY = GROUND - b.h + 26; wY < GROUND - 14; wY += 18) {
          for (let wX = bx + 6; wX < bx + b.w - 6; wX += 12) {
            if ((Math.floor(wX) * 7 + wY * 13) % 5 === 0) ctx.fillRect(wX, wY, 4, 6);
          }
        }
      });
      ctx.fillStyle = "#4a2014";
      g.ruins.forEach((b) => {
        const bx = ((b.x - g.distance * 7) % (W + 220) + (W + 220)) % (W + 220) - 110;
        ctx.fillRect(bx, GROUND - b.h, b.w, b.h);
      });

      // embers and sand motes drifting
      g.ash.forEach((a) => {
        a.y += a.s;
        a.x -= 0.4;
        if (a.y > H) { a.y = -6; a.x = Math.random() * W; }
        if (a.x < -6) a.x = W + 6;
        ctx.fillStyle = "rgba(255, 190, 120, 0.3)";
        ctx.fillRect(a.x, a.y, a.r, a.r);
      });

      // sand and stone ground
      const soil = ctx.createLinearGradient(0, GROUND, 0, H);
      soil.addColorStop(0, "#8a4a1c");
      soil.addColorStop(0.35, "#5e2c10");
      soil.addColorStop(1, "#2e1308");
      ctx.fillStyle = soil;
      ctx.fillRect(-20, GROUND, W + 40, H - GROUND + 20);
      ctx.strokeStyle = "rgba(255, 190, 90, 0.55)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-20, GROUND);
      ctx.lineTo(W + 20, GROUND);
      ctx.stroke();

      // roadside wreckage — rusted cars, bent signs, rocks, robot remains
      g.props.forEach((p) => {
        const px = ((p.x - g.distance * 14) % (W + 260) + (W + 260)) % (W + 260) - 130;
        if (p.kind === "car") {
          ctx.save();
          ctx.translate(px, GROUND);
          ctx.fillStyle = "#5e2412";
          ctx.fillRect(0, -20, 54, 16);
          ctx.fillStyle = "#7c3316";
          ctx.fillRect(10, -32, 30, 12);
          ctx.fillStyle = "rgba(20, 10, 8, 0.9)";
          ctx.fillRect(13, -30, 10, 8);
          ctx.fillRect(26, -30, 10, 8);
          ctx.fillStyle = "#1c0f0a";
          ctx.beginPath();
          ctx.arc(13, -4, 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(42, -4, 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "rgba(255, 140, 60, 0.2)";
          ctx.fillRect(0, -20, 54, 3);
          ctx.restore();
        } else if (p.kind === "sign") {
          ctx.save();
          ctx.translate(px, GROUND);
          ctx.rotate(p.tilt);
          ctx.strokeStyle = "#4a2c14";
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(0, -56);
          ctx.stroke();
          ctx.fillStyle = "#6e3a16";
          ctx.fillRect(-16, -76, 34, 20);
          ctx.strokeStyle = "rgba(255, 190, 90, 0.5)";
          ctx.lineWidth = 1;
          ctx.strokeRect(-16, -76, 34, 20);
          ctx.restore();
        } else if (p.kind === "bot") {
          ctx.save();
          ctx.translate(px, GROUND);
          ctx.fillStyle = "#4a3428";
          ctx.beginPath();
          ctx.arc(0, -10, 16, Math.PI, 0);
          ctx.fill();
          ctx.fillRect(-16, -10, 32, 8);
          ctx.fillStyle = "#160b06";
          ctx.beginPath();
          ctx.arc(-6, -12, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#4a3428";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(8, -24);
          ctx.lineTo(14, -34);
          ctx.stroke();
          ctx.fillStyle = "rgba(255, 153, 0, 0.35)";
          ctx.beginPath();
          ctx.arc(14, -34, 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        } else {
          ctx.fillStyle = "#542412";
          ctx.beginPath();
          ctx.moveTo(px, GROUND);
          ctx.lineTo(px + 8, GROUND - p.h);
          ctx.lineTo(px + p.w - 6, GROUND - p.h * 0.7);
          ctx.lineTo(px + p.w, GROUND);
          ctx.closePath();
          ctx.fill();
        }
      });

      // sand drift streaks
      ctx.strokeStyle = "rgba(40, 16, 8, 0.4)";
      ctx.lineWidth = 1;
      for (let i = 0; i < 12; i += 1) {
        const cx = ((i * 97 - g.distance * 14) % (W + 100) + (W + 100)) % (W + 100) - 50;
        ctx.beginPath();
        ctx.moveTo(cx, GROUND + 16 + (i % 3) * 12);
        ctx.lineTo(cx + 34, GROUND + 16 + (i % 3) * 12);
        ctx.stroke();
      }
      }

      // night shift — dusk swallows the sky as the worm tiers up
      if (g.night > 0.01) {
        ctx.fillStyle = `rgba(8, 6, 32, ${g.night.toFixed(3)})`;
        ctx.fillRect(-20, -20, W + 40, H + 40);
      }

      // coins
      g.coins.forEach((c) => {
        if (c.got) return;
        const pulse = 1 + Math.sin((g.frame + c.x) * 0.15) * 0.15;
        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.scale(pulse, pulse);
        ctx.fillStyle = "rgba(255, 0, 51, 0.25)";
        ctx.beginPath();
        ctx.arc(0, 0, 13, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#ff0033";
        ctx.beginPath();
        ctx.arc(0, 0, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#0b0909";
        ctx.font = "900 8px monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("CR", 0, 0);
        ctx.restore();
      });

      // golden speed cells — rare drops, bolt sigil, hot glow
      g.cells.forEach((c) => {
        if (c.got) return;
        const pulse = 1 + Math.sin((g.frame + c.x) * 0.2) * 0.2;
        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.scale(pulse, pulse);
        ctx.fillStyle = "rgba(255, 153, 0, 0.3)";
        ctx.beginPath();
        ctx.arc(0, 0, 17, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#ffdf6b";
        ctx.beginPath();
        ctx.arc(0, 0, 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#ff9900";
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = "#3d1e00";
        ctx.beginPath();
        ctx.moveTo(2, -7);
        ctx.lineTo(-4, 1);
        ctx.lineTo(0, 1);
        ctx.lineTo(-2, 7);
        ctx.lineTo(4, -1);
        ctx.lineTo(0, -1);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      });

      // ---------- weapon crates — rare supply drops with a temporary MK overdrive ----------
      g.weaponCrates.forEach((c) => {
        if (c.got) return;
        const bob = Math.sin((g.frame + c.x) * 0.08) * 4;
        const cy = GROUND - 16 + bob;
        ctx.save();
        ctx.translate(c.x, cy);
        ctx.fillStyle = "rgba(88, 255, 155, 0.22)";
        ctx.beginPath();
        ctx.arc(0, 0, 24, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#12240f";
        ctx.strokeStyle = "#58ff9b";
        ctx.lineWidth = 2;
        ctx.fillRect(-13, -10, 26, 20);
        ctx.strokeRect(-13, -10, 26, 20);
        ctx.beginPath();
        ctx.moveTo(-13, -1);
        ctx.lineTo(13, -1);
        ctx.moveTo(0, -10);
        ctx.lineTo(0, 10);
        ctx.stroke();
        ctx.fillStyle = "#58ff9b";
        ctx.font = "900 8px monospace";
        ctx.textAlign = "center";
        ctx.fillText("MK", 0, 5);
        ctx.textAlign = "left";
        ctx.restore();
      });

      // ---------- crimson weapon cases — permanent arsenal unlocks ----------
      g.weaponDrops.forEach((c) => {
        if (c.got) return;
        const bob = Math.sin((g.frame + c.x) * 0.08) * 4;
        const cy = GROUND - 16 + bob;
        ctx.save();
        ctx.translate(c.x, cy);
        ctx.fillStyle = "rgba(255, 51, 85, 0.26)";
        ctx.beginPath();
        ctx.arc(0, 0, 26, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#2a0a12";
        ctx.strokeStyle = "#ff3355";
        ctx.lineWidth = 2;
        ctx.fillRect(-14, -11, 28, 22);
        ctx.strokeRect(-14, -11, 28, 22);
        ctx.beginPath();
        ctx.moveTo(-14, 0);
        ctx.lineTo(14, 0);
        ctx.stroke();
        ctx.fillStyle = "#ff8ba0";
        ctx.font = "900 8px monospace";
        ctx.textAlign = "center";
        ctx.fillText(c.weapon === "laser" ? "LS" : c.weapon === "machinegun" ? "MG" : "GC", 0, 4);
        ctx.textAlign = "left";
        ctx.restore();
      });

      // ---------- grenade splash explosions — fireball, shockwave, sparks, smoke ----------
      g.booms.forEach((b, bi) => {
        const t = Math.max(0, Math.min(1, 1 - b.life / 22));
        const fade = Math.max(0, b.life / 22);
        const seed = b.x * 0.73 + b.y * 1.31 + bi * 7.7;
        const rnd = (i) => {
          const v = Math.sin(seed + i * 12.9898) * 43758.5453;
          return v - Math.floor(v);
        };
        const fy = b.y - t * 14; // the fireball climbs as it blooms
        ctx.save();
        // ground scorch — a charred ring that lingers as the blast fades
        if (b.y > GROUND - 70) {
          ctx.fillStyle = `rgba(20, 12, 8, ${(0.4 * fade).toFixed(3)})`;
          ctx.beginPath();
          ctx.ellipse(b.x, GROUND - 2, 24 + t * 34, 6, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        // detonation flash — a blinding white burst in the first instants
        if (t < 0.15) {
          ctx.fillStyle = `rgba(255, 246, 220, ${(((0.15 - t) / 0.15) * 0.85).toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(b.x, b.y, 26 + t * 260, 0, Math.PI * 2);
          ctx.fill();
        }
        // shockwave — a fast thin pressure ring racing ahead of the fire
        if (t < 0.45) {
          const st = t / 0.45;
          ctx.strokeStyle = `rgba(255, 226, 170, ${((1 - st) * 0.75).toFixed(3)})`;
          ctx.lineWidth = 1 + (1 - st) * 5;
          ctx.beginPath();
          ctx.arc(b.x, fy, 24 + st * 150, 0, Math.PI * 2);
          ctx.stroke();
        }
        // fireball — layered combustion: deep red shell, orange body, yellow heart, white core
        const fr = (12 + t * 96) * (1 + Math.sin(g.frame * 1.3 + seed) * 0.04);
        ctx.fillStyle = `rgba(150, 30, 0, ${(0.55 * fade).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(b.x + Math.sin(seed) * 4, fy, fr * 1.15, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(255, 110, 0, ${(0.78 * fade).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(b.x, fy - fr * 0.08, fr * 0.88, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(255, 190, 40, ${(0.9 * fade).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(b.x - fr * 0.06, fy - fr * 0.14, fr * 0.58, 0, Math.PI * 2);
        ctx.fill();
        if (t < 0.55) {
          ctx.fillStyle = `rgba(255, 252, 240, ${((1 - t / 0.55) * 0.95).toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(b.x, fy - fr * 0.1, fr * 0.3, 0, Math.PI * 2);
          ctx.fill();
        }
        // shrapnel sparks — hot streaks flung out on deterministic trajectories
        if (t < 0.6) {
          const st = t / 0.6;
          ctx.lineWidth = 2;
          for (let i = 0; i < 9; i += 1) {
            const ang = rnd(i) * Math.PI * 2;
            const spd = 90 + rnd(i + 20) * 170;
            const d = 12 + st * spd * 0.6;
            ctx.strokeStyle = `rgba(255, ${Math.round(160 + rnd(i + 40) * 80)}, 90, ${((1 - st) * 0.9).toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(b.x + Math.cos(ang) * d, fy + Math.sin(ang) * d);
            ctx.lineTo(b.x + Math.cos(ang) * (d + 9), fy + Math.sin(ang) * (d + 9));
            ctx.stroke();
          }
        }
        // smoke — heavy dark puffs rolling upward as the fire dies
        if (t > 0.35) {
          const st = (t - 0.35) / 0.65;
          for (let i = 0; i < 4; i += 1) {
            const ox = (rnd(i + 60) - 0.5) * 62;
            const sr = 13 + st * 44 + rnd(i + 70) * 8;
            ctx.fillStyle = `rgba(42, 34, 30, ${((1 - st) * 0.42).toFixed(3)})`;
            ctx.beginPath();
            ctx.arc(b.x + ox, fy - st * 52 - rnd(i + 80) * 12, sr, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.restore();
      });

      // ---------- mutated crows — eight-frame flight cycle, diving on Dolo ----------
      if (g.skyFlash > 0) {
        // swoop warning — the sky tears red the moment a crow commits to the dive
        const flash = (g.skyFlash / 16) * 0.3;
        ctx.fillStyle = `rgba(255, 0, 51, ${flash.toFixed(3)})`;
        ctx.fillRect(0, 0, W, Math.round(H * 0.62));
        ctx.fillStyle = `rgba(255, 0, 51, ${(flash * 2.4).toFixed(3)})`;
        ctx.fillRect(0, Math.round(H * 0.62) - 3, W, 3);
      }
      const crowFrames = crowRef.current.filter(Boolean);
      if (crowFrames.length) {
        g.crows.forEach((c) => {
          if (c.dead) return;
          const img = crowFrames[Math.floor((g.frame + c.bob * 20) / 5) % crowFrames.length];
          const cw = 112;
          const chh = cw * (img.height / img.width);
          ctx.save();
          ctx.translate(c.x, c.y);
          ctx.rotate(c.tilt || 0);
          // crimson aura so the silhouette reads against the smog — ember flock burns orange
          ctx.shadowColor = c.ember ? "rgba(255, 110, 0, 0.85)" : "rgba(255, 0, 51, 0.55)";
          ctx.shadowBlur = c.ember ? 22 : 16;
          // sheet art already faces left — the crow flies beak-first at Dolo
          ctx.drawImage(img, -cw / 2, -chh / 2, cw, chh);
          // dive marker — a pulsing fang over the crow for its first stretch of the swoop
          if (c.mode === "dive" && g.frame - (c.diveAt || 0) < 45) {
            const pulse = 0.55 + 0.45 * Math.sin(g.frame * 0.4);
            ctx.fillStyle = `rgba(255, 0, 51, ${pulse.toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(0, -chh / 2 - 20);
            ctx.lineTo(-11, -chh / 2 - 40);
            ctx.lineTo(11, -chh / 2 - 40);
            ctx.closePath();
            ctx.fill();
          }
          if (c.ember) {
            ctx.globalCompositeOperation = "source-atop";
            ctx.fillStyle = "rgba(255, 92, 0, 0.4)";
            ctx.fillRect(-cw / 2, -chh / 2, cw, chh);
            ctx.globalCompositeOperation = "source-over";
          }
          ctx.restore();
        });
      }

      // ---------- mutated chargers — ground packs rushing head-on ----------
      if (g.beastFlash > 0) {
        // charge warning — the road tears red the moment a beast commits
        const bflash = (g.beastFlash / 14) * 0.26;
        ctx.fillStyle = `rgba(255, 0, 51, ${bflash.toFixed(3)})`;
        ctx.fillRect(0, Math.round(H * 0.58), W, H - Math.round(H * 0.58));
      }
      const beastImgs = { wolf: beastRef?.current?.wolf, bear: beastRef?.current?.bear };
      g.beasts.forEach((b) => {
        if (b.dead) return;
        const art = BEAST_ART[b.kind];
        const img = beastImgs[b.kind];
        if (!img) return;
        const fi = Math.floor((g.frame + b.bob) / art.animEvery) % art.frames;
        const bw = art.w;
        const bh = bw * (art.fh / art.fw);
        ctx.save();
        ctx.translate(b.x, GROUND + 6);
        // crimson aura so the pack reads against the dust — a fresh hit flares it
        ctx.shadowColor = b.hitFlash > 0 ? "rgba(255, 60, 60, 0.95)" : b.kind === "bear" ? "rgba(255, 110, 0, 0.8)" : "rgba(255, 0, 51, 0.55)";
        ctx.shadowBlur = b.hitFlash > 0 ? 30 : b.kind === "bear" ? 20 : 14;
        // strip art faces left — the pack charges head-first at Dolo
        ctx.drawImage(img, fi * art.fw, 0, art.fw, art.fh, -bw / 2, -bh, bw, bh);
        ctx.restore();
        // plating pips over a wounded bear so the extra hits read clearly
        if (b.kind === "bear" && b.hp < art.hp) {
          for (let i = 0; i < art.hp; i += 1) {
            ctx.fillStyle = i < b.hp ? "#ff3344" : "rgba(140, 123, 117, 0.3)";
            ctx.fillRect(b.x - 13 + i * 10, GROUND - bh - 8, 7, 5);
          }
        }
      });

      // obstacles (wasteland debris)
      g.obstacles.forEach((o) => {
        ctx.fillStyle = o.hit ? "#3d1518" : "#241a1c";
        ctx.beginPath();
        ctx.moveTo(o.x, GROUND);
        ctx.lineTo(o.x + o.w * 0.25, GROUND - o.h);
        ctx.lineTo(o.x + o.w * 0.7, GROUND - o.h * 0.82);
        ctx.lineTo(o.x + o.w, GROUND);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = "rgba(255, 0, 51, 0.55)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });

      // dust
      g.dust.forEach((d) => {
        ctx.fillStyle = `rgba(200, 150, 95, ${(d.life / 40).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(d.x, d.y, 4 + (22 - d.life) * 0.4, 0, Math.PI * 2);
        ctx.fill();
      });

      // ---------- the worm (official mutated demonic sand worm sprite) ----------
      // boss waves force the Apex Devourer out early, fully enraged
      const bossWave = isBossWave(g);
      const effTier = bossWave ? WORM_TIERS.length - 1 : g.tier;
      const wormX = 150 - g.worm;
      const wormBob = Math.sin(g.frame * 0.09) * 5;
      // ground burst under worm
      ctx.fillStyle = "rgba(200, 150, 95, 0.4)";
      for (let i = 0; i < 5; i += 1) {
        ctx.beginPath();
        ctx.arc(wormX - 60 + i * 22 + Math.sin(g.frame * 0.3 + i) * 4, GROUND + 4, 5 + (i % 3) * 2, 0, Math.PI * 2);
        ctx.fill();
      }
      const tier = WORM_TIERS[effTier];
      let wormImg = null;
      for (let i = effTier; i >= 0; i -= 1) {
        if (wormsRef.current[i]) {
          wormImg = wormsRef.current[i];
          break;
        }
      }
      if (wormImg) {
        const wh = tier.h;
        const ww = wh * (wormImg.width / wormImg.height);
        ctx.save();
        ctx.imageSmoothingEnabled = false;
        if (g.worm < 100 || g.tier >= 2 || bossWave) {
          ctx.shadowColor = "rgba(255, 0, 51, 0.55)";
          ctx.shadowBlur = bossWave ? 46 : 26 + g.tier * 4;
        }
        ctx.drawImage(wormImg, wormX + 46 - ww, GROUND + 16 - wh + wormBob, ww, wh);
        ctx.restore();
      } else {
        ctx.fillStyle = "#2e2126";
        ctx.beginPath();
        ctx.arc(wormX, GROUND - 52 + wormBob, 36, 0, Math.PI * 2);
        ctx.fill();
      }

      // ---------- BOSS WORM — every other delivery a named boss erupts ahead for a duel ----------
      // the first two bosses carry full pose sets (roar on the burrow-in, attack on the slam,
      // damage on hits, a death pose as the body drops); the rest are static artworks
      if (g.wormBoss) {
        const wb = g.wormBoss;
        const art = BOSS_WORMS[wb.variant];
        const bossBob = wb.pose === "idle" ? Math.sin(g.frame * 0.06) * 5 : 0;
        const entry = bossRef?.current?.[wb.variant];
        const bossImg = entry ? (entry.idle ? entry[wb.pose] || entry.idle : entry) : null;
        if (bossImg) {
          const bh = art.h;
          const bw = bh * (bossImg.width / bossImg.height);
          ctx.save();
          ctx.imageSmoothingEnabled = false;
          ctx.shadowColor = wb.flash > 0 ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 0, 51, 0.6)";
          ctx.shadowBlur = wb.flash > 0 ? 46 : 34;
          if (wb.flash > 0) ctx.filter = "brightness(1.9) saturate(1.6)";
          // sprite base sits on the ground line — the art carries its own sand mound
          ctx.drawImage(bossImg, wb.x + 40 - bw, GROUND + 6 - bh + bossBob, bw, bh);
          ctx.restore();
        } else {
          ctx.fillStyle = "#3a1a1e";
          ctx.beginPath();
          ctx.arc(wb.x - 60, GROUND - 120, 90, 0, Math.PI * 2);
          ctx.fill();
        }
        // weak point window — a pulsing gold glow on the boss's face; hits at this height crit ×2
        if (wb.weakWin > 0) {
          const wx = wb.x - 185;
          const wy = GROUND - wb.weakY;
          const pulse = 1 + Math.sin(g.frame * 0.4) * 0.18;
          ctx.save();
          ctx.globalAlpha = 0.55 + Math.sin(g.frame * 0.4) * 0.25;
          ctx.strokeStyle = "#ffd23d";
          ctx.lineWidth = 2.5;
          ctx.setLineDash([7, 5]);
          ctx.beginPath();
          ctx.arc(wx, wy, 30 * pulse, g.frame * 0.08, g.frame * 0.08 + Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = "rgba(255, 210, 61, 0.28)";
          ctx.beginPath();
          ctx.arc(wx, wy, 22 * pulse, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#fff3c4";
          ctx.beginPath();
          ctx.arc(wx, wy, 7, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }

      // boss death beat — the carcass drops in its death pose, sinks and fades
      if (g.bossDying) {
        const d = g.bossDying;
        const art = BOSS_WORMS[d.variant];
        const entry = bossRef?.current?.[d.variant];
        const deathImg = entry ? (entry.idle ? entry.death || entry.idle : entry) : null;
        if (deathImg) {
          const bh = art.h;
          const bw = bh * (deathImg.width / deathImg.height);
          const sink = (1 - d.t / 55) * 12;
          ctx.save();
          ctx.imageSmoothingEnabled = false;
          ctx.globalAlpha = Math.min(1, d.t / 25);
          ctx.filter = "brightness(0.85)";
          ctx.drawImage(deathImg, d.x + 40 - bw, GROUND + 6 - bh + sink, bw, bh);
          ctx.restore();
        }
      }

      // weak-crit floaters — golden damage callouts drifting off the impact point
      g.crits.forEach((c) => {
        ctx.save();
        ctx.globalAlpha = Math.min(1, c.life / 26);
        ctx.fillStyle = "#ffd23d";
        ctx.font = "900 13px monospace";
        ctx.textAlign = "center";
        ctx.fillText(c.txt, c.x, c.y);
        ctx.restore();
      });

      // boss shockwaves — rolling dust walls that must be jumped
      g.bossShocks.forEach((s) => {
        ctx.fillStyle = "rgba(255, 0, 51, 0.5)";
        ctx.fillRect(s.x - 34, GROUND - 6, 68, 5);
        ctx.fillStyle = "rgba(200, 150, 95, 0.9)";
        for (let i = 0; i < 6; i += 1) {
          const hgt = 26 + Math.sin(g.frame * 0.5 + i * 1.7) * 10 + (i % 2) * 12;
          ctx.beginPath();
          ctx.arc(s.x - 25 + i * 10, GROUND + 2 - hgt / 2, 8 + (i % 3) * 3, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // ---------- boombox fireballs (larger + missile trail after each delivery upgrade) ----------
      g.shots.forEach((s) => {
        if (s.type === "grenade") {
          // grenade cassette — dark tape shell, amber stripe, blinking armed light
          ctx.save();
          ctx.translate(s.x, s.y);
          ctx.rotate(g.frame * 0.25);
          ctx.fillStyle = "#151515";
          ctx.strokeStyle = "#ffb42a";
          ctx.lineWidth = 1.5;
          ctx.fillRect(-7, -5, 14, 10);
          ctx.strokeRect(-7, -5, 14, 10);
          ctx.fillStyle = "#ffb42a";
          ctx.fillRect(-7, -1.5, 14, 3);
          ctx.restore();
          if (Math.floor(g.frame / 10) % 2 === 0) {
            ctx.fillStyle = "#ff3355";
            ctx.beginPath();
            ctx.arc(s.x, s.y - 9, 2.5, 0, Math.PI * 2);
            ctx.fill();
          }
          return;
        }
        const sr = s.r || 1;
        const flick = (1 + Math.sin(g.frame * 0.9 + s.x) * 0.18) * sr;
        ctx.save();
        ctx.translate(s.x, s.y);
        ctx.scale(flick, flick);
        if (s.bossUp) {
          // BOSS-LEVEL arsenal override — the payload that cracks 10,000 hull:
          // machine gun fires rainbow tracers, the boombox hurls blue plasma bolts
          const mg = g.ranged === "machinegun";
          const hue = (g.frame * 12 + s.x * 2) % 360;
          ctx.fillStyle = mg ? `hsla(${hue}, 100%, 62%, 0.55)` : "rgba(90, 170, 255, 0.45)";
          ctx.beginPath();
          ctx.ellipse(-Math.sign(s.vx) * 20, 0, 24, 6, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = mg ? `hsla(${(hue + 120) % 360}, 100%, 65%, 0.9)` : "#4aa3ff";
          ctx.beginPath();
          ctx.arc(0, 0, 9, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(0, 0, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          return;
        }
        if (s.missile) {
          ctx.fillStyle = "rgba(255, 60, 0, 0.55)";
          ctx.beginPath();
          ctx.ellipse(-Math.sign(s.vx) * 15, 0, 14, 4.5, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = "rgba(255, 120, 0, 0.35)";
        ctx.beginPath();
        ctx.arc(0, 0, 15, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#ff9900";
        ctx.beginPath();
        ctx.arc(0, 0, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#fff3c4";
        ctx.beginPath();
        ctx.arc(0, 0, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      // ---------- dolo (boombox-gunner sprite set) ----------
      const airborne = g.y < GROUND - 118;
      const bob = airborne ? 0 : Math.sin(g.frame * 0.35) * 4;
      const tilt = airborne && g.slash <= 0 ? Math.max(-0.22, Math.min(0.28, g.vy * 0.02)) : 0;
      ctx.save();
      ctx.translate(150 + 48, g.y + 59 + bob);
      ctx.rotate(tilt);
      if (g.invuln > 0 && Math.floor(g.frame / 5) % 2 === 0) ctx.globalAlpha = 0.35;
      const fr = doloRef.current;
      let dimg = null;
      if (g.slash > 0 && fr.slash.length) {
        dimg = fr.slash[Math.min(3, Math.floor(((14 - g.slash) / 14) * 4))] || null;
      }
      if (!dimg && g.shootAnim > 0) {
        const set = g.shootDir < 0 ? fr.back : fr.fwd;
        dimg = set[Math.floor((16 - g.shootAnim) / 4) % 4] || null;
      }
      if (!dimg && airborne) dimg = fr.jump;
      if (!dimg && fr.run.length) dimg = fr.run[Math.floor(g.frame / 7) % 2];
      if (dimg && dimg.width) {
        ctx.imageSmoothingEnabled = false;
        const dw = 118 * (dimg.width / dimg.height);
        ctx.drawImage(dimg, -dw / 2, -59, dw, 118);
        ctx.filter = "none";
        // ---------- real red laser blade — a plasma edge that stretches the more it is swung ----------
        if (g.slash > 0 && g.laserSlash) {
          ctx.imageSmoothingEnabled = true;
          const p = (14 - g.slash) / 14; // sweep progress through the slash frames
          const ang = -1.5 + p * 2.2; // overhead ignition sweeping down through the cut
          const ext = Math.min(1, 0.3 + p * 2.8); // the blade snaps out at ignition
          const flick = 1 + Math.sin(g.frame * 1.7) * 0.02;
          const L = laserReach(g) * 0.85 * ext * flick;
          const hx = 14;
          const hy = -12; // hilt in Dolo's leading hand
          const tx = hx + Math.cos(ang) * L;
          const ty = hy + Math.sin(ang) * L;
          // red light spilling across the dust under the blade
          ctx.fillStyle = "rgba(255, 0, 45, 0.1)";
          ctx.beginPath();
          ctx.ellipse(hx + L * 0.4, GROUND - (g.y + 59 + bob) - 3, L * 0.42, 5, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.lineCap = "round";
          ctx.strokeStyle = "rgba(255, 0, 45, 0.16)";
          ctx.lineWidth = 18;
          ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(tx, ty); ctx.stroke();
          ctx.strokeStyle = "rgba(255, 20, 60, 0.45)";
          ctx.lineWidth = 10;
          ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(tx, ty); ctx.stroke();
          ctx.strokeStyle = "rgba(255, 90, 110, 0.85)";
          ctx.lineWidth = 5;
          ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(tx, ty); ctx.stroke();
          ctx.strokeStyle = "#fff5f6";
          ctx.lineWidth = 2.2;
          ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(tx, ty); ctx.stroke();
          // flare at the tip and the emitter glow at the hilt
          ctx.fillStyle = "rgba(255, 225, 228, 0.95)";
          ctx.beginPath();
          ctx.arc(tx, ty, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "rgba(255, 40, 70, 0.9)";
          ctx.beginPath();
          ctx.arc(hx, hy, 4.5, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (g.spriteReady) {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(spriteRef.current, -48, -59, 97, 118);
      } else {
        ctx.fillStyle = "#ff0033";
        ctx.fillRect(-24, -59, 48, 118);
      }
      ctx.restore();

      ctx.restore();

      // ---------- HUD (screen space — no camera drift) ----------
      // the score block lives in the DOM above the canvas (black frame top bar) —
      // the playfield stays completely clear; only transient banners paint here
      if (phaseRef.current === "running") {
        ctx.save();
        ctx.scale(scale, scale);
        ctx.font = "700 10px monospace";
        ctx.textAlign = "left";

        // speed-cell charged flash
        if (g.boostFlash > 0 && Math.floor(g.boostFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(255, 153, 0, 0.14)";
          ctx.fillRect(0, 138, W, 30);
          ctx.fillStyle = "#ff9900";
          ctx.font = "900 13px monospace";
          ctx.textAlign = "center";
          ctx.fillText("SPEED CELL CHARGED — SHIFT / BOLT TO BURST", W / 2, 158);
          ctx.textAlign = "left";
        }

        // worm repelled flash
        if (g.repelFlash > 0 && Math.floor(g.repelFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(255, 0, 51, 0.16)";
          ctx.fillRect(0, 176, W, 32);
          ctx.fillStyle = "#ffffff";
          ctx.font = "900 14px monospace";
          ctx.textAlign = "center";
          ctx.fillText("WORM REPELLED — PLATING SHATTERED +250", W / 2, 197);
          ctx.textAlign = "left";
        }

        // boss kill bounty flash — enraged Apex plating shattered during a boss wave
        if (g.bountyFlash > 0 && Math.floor(g.bountyFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(255, 196, 0, 0.2)";
          ctx.fillRect(0, 262, W, 34);
          ctx.fillStyle = "#ffc400";
          ctx.font = "900 14px monospace";
          ctx.textAlign = "center";
          ctx.fillText("BOSS PLATING SHATTERED — BOUNTY PAID +15 PTS · +625 CR", W / 2, 284);
          ctx.textAlign = "left";
        }

        // threat escalation flash
        if (g.tierFlash > 0 && Math.floor(g.tierFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(255, 0, 51, 0.14)";
          ctx.fillRect(0, 96, W, 34);
          ctx.fillStyle = "#ff0033";
          ctx.font = "900 15px monospace";
          ctx.textAlign = "center";
          ctx.fillText(`!! THREAT ESCALATION — ${WORM_TIERS[g.tier].name} CLASS EMERGED !!`, W / 2, 118);
          ctx.textAlign = "left";
        }

        // delivery complete flash — round logged, route progress
        if (g.deliveryFlash > 0 && Math.floor(g.deliveryFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(255, 153, 0, 0.2)";
          ctx.fillRect(0, 214, W, 44);
          ctx.fillStyle = "#ffb42a";
          ctx.font = "900 15px monospace";
          ctx.textAlign = "center";
          ctx.fillText(`DELIVERY ${g.delivery - 1} COMPLETE — THE ROUND IS LOGGED`, W / 2, 232);
          ctx.fillStyle = "rgba(255, 243, 196, 0.85)";
          ctx.font = "700 10px monospace";
          ctx.fillText(`${DELIVERY_MAX - (g.delivery - 1)} DELIVERIES TO ROUTE CLEARANCE`, W / 2, 248);
          ctx.textAlign = "left";
        }

        // weapon upgrade flash — every 10,000 PTS the boombox levels up
        if (g.weaponFlash > 0 && Math.floor(g.weaponFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(88, 255, 155, 0.14)";
          ctx.fillRect(0, 300, W, 32);
          ctx.fillStyle = "#58ff9b";
          ctx.font = "900 13px monospace";
          ctx.textAlign = "center";
          ctx.fillText(`WEAPON UPGRADE — BOOMBOX MK-${g.weaponLevel} · HARDER HITS · BIGGER FIRE · FASTER CYCLE`, W / 2, 321);
          ctx.textAlign = "left";
        }

        // supply crate flash — temporary MK overdrive active
        if (g.crateFlash > 0 && Math.floor(g.crateFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(88, 255, 155, 0.2)";
          ctx.fillRect(0, 340, W, 32);
          ctx.fillStyle = "#58ff9b";
          ctx.font = "900 13px monospace";
          ctx.textAlign = "center";
          ctx.fillText(`SUPPLY CRATE — MK OVERDRIVE +${WEAPON_CRATE_BOOST} FOR ${WEAPON_CRATE_SECONDS}S`, W / 2, 361);
          ctx.textAlign = "left";
        }

        // weapon secured flash — permanent arsenal unlock
        if (g.dropFlash > 0 && Math.floor(g.dropFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(255, 51, 85, 0.2)";
          ctx.fillRect(0, 380, W, 32);
          ctx.fillStyle = "#ff8ba0";
          ctx.font = "900 13px monospace";
          ctx.textAlign = "center";
          ctx.fillText(`WEAPON SECURED — ${g.dropName} ADDED TO ARSENAL · SELECT IT ANYTIME`, W / 2, 401);
          ctx.textAlign = "left";
        }

        // boss wave flash — every 5th delivery the Apex Devourer hunts enraged
        if (g.bossFlash > 0 && Math.floor(g.bossFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(255, 0, 51, 0.22)";
          ctx.fillRect(0, 214, W, 44);
          ctx.fillStyle = "#ff0033";
          ctx.font = "900 15px monospace";
          ctx.textAlign = "center";
          ctx.fillText(`DELIVERY ${g.delivery - 1} COMPLETE — !! BOSS WAVE: APEX DEVOURER ENRAGED !!`, W / 2, 232);
          ctx.fillStyle = "rgba(255, 243, 196, 0.85)";
          ctx.font = "700 10px monospace";
          ctx.fillText(`IT HUNTS HARDER UNTIL DELIVERY ${g.delivery} LANDS — HITS PUSH IT BACK LESS`, W / 2, 248);
          ctx.textAlign = "left";
        }

        // boss worm engaged — every other delivery a named boss erupts ahead
        if (g.bossEngageFlash > 0 && Math.floor(g.bossEngageFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(255, 0, 51, 0.24)";
          ctx.fillRect(0, 214, W, 44);
          ctx.fillStyle = "#ff3344";
          ctx.font = "900 15px monospace";
          ctx.textAlign = "center";
          ctx.fillText(`DELIVERY ${g.delivery - 1} COMPLETE — !! BOSS WORM: ${g.wormBoss ? BOSS_WORMS[g.wormBoss.variant].name : ""} ERUPTS !!`, W / 2, 232);
          ctx.fillStyle = "rgba(255, 243, 196, 0.85)";
          ctx.font = "700 10px monospace";
          ctx.fillText(`10,000 HULL — ARSENAL OVERDRIVEN TO BOSS LEVEL · JUMP THE SHOCKWAVES · HOLD FIRE`, W / 2, 248);
          ctx.textAlign = "left";
        }

        // boss worm destroyed — hull broken, bounty paid, arsenal back to standard
        if (g.bossKillFlash > 0 && Math.floor(g.bossKillFlash / 8) % 2 === 0) {
          ctx.fillStyle = "rgba(88, 255, 155, 0.18)";
          ctx.fillRect(0, 214, W, 44);
          ctx.fillStyle = "#58ff9b";
          ctx.font = "900 15px monospace";
          ctx.textAlign = "center";
          ctx.fillText(`BOSS WORM DESTROYED — ${g.bossKillName} — +1,000 CR · +20 PTS`, W / 2, 232);
          ctx.fillStyle = "rgba(255, 243, 196, 0.85)";
          ctx.font = "700 10px monospace";
          ctx.fillText(`ARSENAL BACK TO STANDARD ISSUE — THE ROUTE RESUMES`, W / 2, 248);
          ctx.textAlign = "left";
        }
        ctx.restore();
      }

      // burst speed lines (device space)
      if (g.boost > 0 && phaseRef.current === "running" && !pausedRef.current) {
        ctx.save();
        ctx.strokeStyle = "rgba(255, 153, 0, 0.35)";
        ctx.lineWidth = 2;
        for (let i = 0; i < 9; i += 1) {
          const ly = Math.random() * canvas.height;
          const len = 40 + Math.random() * 120;
          const lx = canvas.width - ((g.frame * 34 + i * 211) % (canvas.width + 300));
          ctx.beginPath();
          ctx.moveTo(lx, ly);
          ctx.lineTo(lx - len, ly);
          ctx.stroke();
        }
        ctx.restore();
      }

      // near-death vignette pulse (device space)
      if (g.slowmo > 0 && phaseRef.current === "running") {
        const pulse = 0.22 + 0.18 * Math.sin(g.frame * 0.35);
        const vignette = ctx.createRadialGradient(
          canvas.width / 2, canvas.height / 2, canvas.height * 0.28,
          canvas.width / 2, canvas.height / 2, canvas.height * 0.78
        );
        vignette.addColorStop(0, "rgba(255, 0, 51, 0)");
        vignette.addColorStop(1, `rgba(255, 0, 51, ${pulse.toFixed(3)})`);
        ctx.fillStyle = vignette;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // paused veil (device space)
      if (pausedRef.current && phaseRef.current === "running") {
        ctx.fillStyle = "rgba(3, 3, 3, 0.6)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#ff0033";
        ctx.font = `900 ${Math.round(20 * scale)}px monospace`;
        ctx.textAlign = "center";
        ctx.fillText("PAUSED — SIGNAL FROZEN", canvas.width / 2, canvas.height / 2 - 8 * scale);
        ctx.fillStyle = "rgba(140, 123, 117, 0.9)";
        ctx.font = `${Math.round(11 * scale)}px monospace`;
        ctx.fillText("TAP / P TO RESUME", canvas.width / 2, canvas.height / 2 + 16 * scale);
        ctx.textAlign = "left";
      }
}
